// The Atlas HUD overlay: the DOM scaffold renderAtlasTab mounts once (breadcrumb,
// hero, inspector, plot controls) and the <style> block it injects for the T5
// controls. Markup and CSS text only, moved verbatim - no behaviour of its own.

import { TEST_SWITCHES } from '../urlFlags.ts';

/** `buildRows` is the ground game's build and forge rows (`panels/build.ts`), or empty. */
export function overlayMarkup(buildRows: string): string {
  return `
    <div class="atlas-top">
      <div class="atlas-breadcrumb" id="atlas-breadcrumb"></div>
      <div class="atlas-top-chips">
        <div class="atlas-badge" id="atlas-badge"></div>
        <div class="atlas-tier" id="atlas-tier"></div>
      </div>
    </div>
    <div class="atlas-clock-hold hidden" id="atlas-clock-hold" role="status"></div>
    <div class="atlas-hero" id="atlas-hero">
      <div class="atlas-hero-title">ATLAS</div>
      <div class="atlas-hero-sub">THE SOLAR SYSTEM</div>
      <div class="atlas-hero-cta" id="atlas-hero-cta">Double-click <strong>Mars</strong> to descend.</div>
    </div>
    <div class="atlas-inspector" id="atlas-inspector"></div>
    <div class="atlas-bottom">
      <button class="atlas-zoom-out" id="atlas-zoom-out" title="Zoom out (Esc)">&#x2191; Zoom Out</button>
      <span class="atlas-warp-host" id="atlas-warp-host"></span>
      <span class="atlas-sf-host" id="atlas-sf-host"></span>
      <span class="atlas-vl-host" id="atlas-vl-host"></span>
      <span class="atlas-era" id="atlas-era"></span>
    </div>
    <div class="atlas-credit hidden" id="atlas-pyramid-credit"></div>
    <div class="atlas-event-log" id="atlas-event-log"></div>
    <div class="atlas-sampler" id="atlas-sampler">
      <div class="hud-head" id="gp-head"><span>GROUND</span><button class="hud-min-btn" id="gp-minimize"
        aria-label="Minimize the ground panel" title="Minimize"><span class="hud-min-glyph"></span></button></div>
      <div class="atlas-sampler-body" id="atlas-sampler-body"></div>
    </div>
    <div class="atlas-telem hidden" id="atlas-telem"></div>
    <div class="atlas-plotctl hidden" id="atlas-plotctl">
      <div class="hud-head" id="pc-head"><span>PLOT CONTROLS</span><button class="hud-min-btn" id="pc-minimize"
        aria-label="Minimize the plot controls" title="Minimize"><span class="hud-min-glyph"></span></button></div>
      <label class="sol-only" id="pc-sol-row">SOL<input id="pc-sol" type="range" min="0" max="1000" value="340"><b id="pc-sol-v"></b></label>
      <label id="pc-ls-row">SEASON<input id="pc-ls" type="range" min="0" max="359" value="120"><b id="pc-ls-v"></b></label>
      ${TEST_SWITCHES ? '<label id="pc-ex-row">RELIEF<input id="pc-ex" type="range" min="10" max="60" value="10"><b id="pc-ex-v"></b></label>' : ''}
      <div class="atlas-plotctl-toggles" id="pc-clock-row">
        <button id="pc-play-sol" class="sol-only">&#9654; SOL</button>
        <button id="pc-play-year">&#9654; YEAR</button>
      </div>
      <div class="atlas-credit hidden" id="pc-sol-note"></div>
      <div class="atlas-plotctl-toggles plot-only" id="pc-fly-row">
        <button id="pc-fly" class="wide">&#9654;&#9654; FLYOVER</button>
      </div>
      <div class="atlas-plotctl-sites plot-only" id="pc-sites">
        <div class="atlas-sites-head hidden" id="pc-sites-head"></div>
        <div class="atlas-sites-search hidden" id="pc-sites-search">
          <input class="atlas-sites-filter hidden" id="pc-sites-filter" type="search" autocomplete="off"
            placeholder="FIND A SITE BY NAME OR ALIAS" aria-label="Find a site by name, alias, mission or product id">
          <button id="pc-sites-more" class="atlas-sites-more" aria-expanded="false" aria-controls="pc-sites-facets"
            title="Filter sites by hemisphere, chart, source and kind, or sort the list.">FILTERS</button>
        </div>
        <div class="atlas-sites-facets hidden" id="pc-sites-facets">
          <select id="pc-sites-ns" aria-label="Hemisphere, north or south"></select>
          <select id="pc-sites-ew" aria-label="Hemisphere, east or west"></select>
          <select id="pc-sites-chart" aria-label="Quadrangle chart"></select>
          <select id="pc-sites-source" aria-label="Source instrument and resolution"></select>
          <select id="pc-sites-kind" aria-label="Kind of site"></select>
          <select id="pc-sites-sort" aria-label="Sort the list"></select>
          <button id="pc-sites-globe" class="atlas-sites-globe" title="Click a point on the body to list the sites near it.">PICK ON GLOBE</button>
        </div>
        <div class="atlas-sites-pick hidden" id="pc-sites-pick"></div>
        <div class="atlas-sites-pin hidden" id="pc-sites-pin"></div>
        <div class="atlas-sites-list" id="pc-sites-list" aria-label="Surveyed sites"></div>
      </div>
      <div class="atlas-plotctl-toggles plot-only" id="pc-overlay-row">
        <button id="pc-overlay">SHOW PLOT LINES</button>
        <button id="pc-contour">CONTOURS</button>
      </div>
      <div class="atlas-plotctl-toggles plot-only" id="pc-cells-row">
        <button id="pc-cells" class="wide" title="How the picked cell and the cell under the cursor are drawn: fill and edge, edge only, or neither. A double-click still picks with this off.">CELLS: FILL + EDGE</button>
      </div>
${buildRows}
      <div class="atlas-plotctl-toggles plot-only" id="pc-rover-row">
        <button id="pc-rover" class="wide">&#9658; DEPLOY ROVER</button>
      </div>
      <div class="atlas-plotctl-toggles plot-only hidden" id="pc-skin-row">
        <button id="pc-skin-built" class="on">BUILT</button>
        <button id="pc-skin-curiosity">CURIOSITY</button>
      </div>
      <div class="atlas-plotctl-toggles plot-only hidden" id="pc-auto-row">
        <button id="pc-auto" class="wide">&#9654; AUTO SURVEY</button>
      </div>
      <div class="atlas-plotctl-toggles plot-only hidden" id="pc-jump-row">
        <button id="pc-jump-rover" class="wide">&#9678; JUMP TO ROVER</button>
      </div>
      <div class="atlas-credit plot-only hidden" id="pc-credit"></div>
      <div class="atlas-plotctl-toggles plot-only" id="pc-rocks-row">
        <button id="pc-rocks-open" class="wide">&#9662; ROCKS</button>
      </div>
      <div class="atlas-rockctl plot-only hidden" id="atlas-rockctl">
        <label>COUNT<input id="rk-density" type="range" min="10" max="400" value="100"><b id="rk-density-v"></b></label>
        <label>SIZE<input id="rk-size" type="range" min="30" max="400" value="100"><b id="rk-size-v"></b></label>
        <label>SPREAD<input id="rk-spread" type="range" min="40" max="260" value="100"><b id="rk-spread-v"></b></label>
        <label>SLOPE<input id="rk-slope" type="range" min="5" max="60" value="20"><b id="rk-slope-v"></b></label>
        <label>BURIAL<input id="rk-burial" type="range" min="0" max="45" value="18"><b id="rk-burial-v"></b></label>
        <label>BUDGET<input id="rk-budget" type="range" min="2" max="60" value="25"><b id="rk-budget-v"></b></label>
        <div class="atlas-plotctl-toggles">
          <button id="rk-copy">COPY MD</button>
          <button id="rk-reset">RESET</button>
        </div>
      </div>
    </div>
  `;
}

export function injectPlotStyles(): void {
  if (document.getElementById('atlas-plot-styles')) return;
  const s = document.createElement('style');
  s.id = 'atlas-plot-styles';
  s.textContent = `
  /* Top of the left column, bounded by the readout slot below it: unbounded it
     grew off the top of the pane and took its sliders out of reach. The width
     bound is the forge row, the widest row the panel draws; past it a long
     line wraps rather than pushing the panel across the ground view. */
  .atlas-plotctl{position:absolute;left:16px;top:var(--hud-top);z-index:6;display:flex;flex-direction:column;gap:5px;
    box-sizing:border-box;max-width:420px;max-height:calc(100% - var(--hud-top) - var(--hud-insp) - 28px);
    overflow-y:auto;overscroll-behavior:contain;
    padding:12px 14px;border:1px solid rgba(255,180,120,.20);border-radius:5px;
    background:rgba(10,7,6,.80);backdrop-filter:blur(9px);
    font:10px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;color:#e8d8c8;letter-spacing:.09em;}
  .atlas-plotctl.hidden{display:none;}
  /* The header the collapse rule in index.html keys on, in the Atlas's amber
     rather than the sim HUD's cyan. Only the colours are ours; the type, the
     rule under it and the button are the shared family's. */
  .atlas-plotctl .hud-head{margin-bottom:2px;color:#ffb454;
    border-bottom-color:rgba(255,180,120,.24);text-shadow:0 0 6px rgba(255,180,84,.30);}
  /* The rover panel's own rows (skin, auto survey, jump to rover) carry this
     class straight from setDriving, with no selector to act on it. */
  .atlas-plotctl-toggles.hidden{display:none;}
  /* One rule per row set, and the rule is HIDE rather than disable: a control
     that is on screen and does nothing reads as broken. .plot-only is every row
     that acts on a loaded plot field or on a site, which a body with no
     sites.json row has neither of; .sol-only is the clock's own scrubber,
     which needs a solar day the body's row can answer for. The !important is
     because #pc-forge-row's own display:grid above is an ID rule and outranks
     three classes on specificity alone - without it the forge row never hid. */
  .atlas-plotctl.no-plot .plot-only{display:none !important;}
  .atlas-plotctl.no-sol .sol-only{display:none !important;}
  .atlas-plotctl::-webkit-scrollbar,.atlas-sampler-body::-webkit-scrollbar,.atlas-sites-list::-webkit-scrollbar{width:6px;}
  .atlas-plotctl::-webkit-scrollbar-thumb,.atlas-sampler-body::-webkit-scrollbar-thumb,.atlas-sites-list::-webkit-scrollbar-thumb{
    background:rgba(255,180,84,.62);border-radius:3px;}
  .atlas-plotctl::-webkit-scrollbar-track,.atlas-sampler-body::-webkit-scrollbar-track,.atlas-sites-list::-webkit-scrollbar-track{
    background:rgba(255,180,120,.08);}
  .atlas-plotctl label{display:grid;grid-template-columns:56px 148px 46px;align-items:center;gap:9px;
    color:rgba(232,216,200,.62);}
  .atlas-plotctl b{color:#ffb454;font-weight:500;text-align:right;}
  .atlas-plotctl input[type=range]{-webkit-appearance:none;appearance:none;height:2px;
    background:rgba(255,180,120,.26);border-radius:2px;outline:none;}
  .atlas-plotctl input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:11px;height:11px;
    border-radius:50%;background:#ffb454;cursor:pointer;box-shadow:0 0 7px rgba(255,180,84,.65);}
  .atlas-plotctl input[type=range]::-moz-range-thumb{width:11px;height:11px;border:0;border-radius:50%;
    background:#ffb454;cursor:pointer;}
  /* Disabled rather than hidden where the row's title says why: SEASON and
     YEAR on a body with no season on record. */
  .atlas-plotctl input[type=range]:disabled,.atlas-plotctl-toggles button:disabled{opacity:.35;
    cursor:not-allowed;}
  /* Rock tuning shares the plot control's typography and grid so it reads as a
     drawer of the same panel rather than a second window. */
  .atlas-rockctl{display:flex;flex-direction:column;gap:7px;margin-top:4px;padding-top:8px;
    border-top:1px solid rgba(255,180,120,.18);}
  .atlas-rockctl.hidden{display:none;}
  .atlas-rockctl label{display:grid;grid-template-columns:56px 148px 46px;align-items:center;gap:9px;
    color:rgba(232,216,200,.62);}
  .atlas-rockctl b{color:#8fd0e0;font-weight:500;text-align:right;}
  .atlas-rockctl input[type=range]{-webkit-appearance:none;appearance:none;height:2px;
    background:rgba(143,208,224,.26);border-radius:2px;outline:none;}
  .atlas-rockctl input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:11px;height:11px;
    border-radius:50%;background:#8fd0e0;cursor:pointer;box-shadow:0 0 7px rgba(143,208,224,.55);}
  .atlas-rockctl input[type=range]::-moz-range-thumb{width:11px;height:11px;border:0;border-radius:50%;
    background:#8fd0e0;cursor:pointer;}
  /* Bottom centre, between the two columns: the left column is already the
     plot controls plus the readout slot, and telemetry over either of them was
     the widest overlap on the pane. */
  .atlas-telem{position:absolute;left:calc(50% - 115px);bottom:var(--hud-bot);z-index:7;width:230px;
    box-sizing:border-box;max-height:calc(100% - var(--hud-top) - var(--hud-bot) - 24px);
    overflow-y:auto;overscroll-behavior:contain;
    padding:10px 12px;border:1px solid rgba(143,208,224,.22);border-radius:5px;
    background:rgba(8,10,14,.84);backdrop-filter:blur(9px);cursor:move;
    font:10px/1.55 ui-monospace,SFMono-Regular,Menlo,monospace;color:#cfe6ee;letter-spacing:.07em;}
  .atlas-telem.hidden{display:none;}
  .tl-head{color:#8fd0e0;margin-bottom:6px;letter-spacing:.16em;}
  .tl-sep{margin:7px 0 3px;color:rgba(143,208,224,.45);font-size:9px;letter-spacing:.14em;
    border-top:1px solid rgba(143,208,224,.14);padding-top:5px;}
  .tl-row{display:flex;justify-content:space-between;gap:10px;color:rgba(207,230,238,.60);}
  .tl-row b{font-weight:500;color:#cfe6ee;}
  .tl-ok{color:#7fd6a2 !important;}
  .tl-warn{color:#ffb454 !important;}
  .atlas-credit{margin-top:5px;font-size:9px;line-height:1.5;color:rgba(232,216,200,.50);
    max-width:250px;letter-spacing:.05em;}
  .atlas-credit.hidden{display:none;}
  .atlas-credit a{color:rgba(143,208,224,.85);text-decoration:none;}
  .atlas-credit a:hover{text-decoration:underline;}
  /* A sibling of .atlas-bottom, not a child of it, so it can move to the
     inspector's own corner when that panel gives up the ground (below) rather
     than fight the sampler and the rover telemetry for the same one. */
  #atlas-pyramid-credit{position:absolute;right:18px;bottom:56px;
    margin-top:0;text-align:right;max-width:230px;}
  /* One phrase never breaks mid-link: getBoundingClientRect on a wrapped inline
     element spans every line it touches, which reads as overlapping the phrase
     below it even though no glyph does. Wrapping happens between phrases, at
     the plain space after each one's full stop, never inside one. Which is why
     the product in each phrase is cut to CREDIT_PRODUCT_CHARS: a nowrap run
     longer than this box leaves it, and letting the phrases wrap instead grows
     the block into the sampler. */
  /* The span is the same phrase for a source with no published url. */
  #atlas-pyramid-credit a,#atlas-pyramid-credit span{white-space:nowrap;}
  /* The view-layer panel. Closed it is one button standing where the hex
     toggle used to, so the bottom bar keeps its shape; open it lifts its own
     list off the bar rather than growing it. */
  .atlas-vl{position:relative;}
  .atlas-vl-head{display:block;background:rgba(8,16,28,.7);border:none;
    box-shadow:inset 0 0 0 1px var(--hud-border);color:var(--hud-text);
    font-family:inherit;font-size:11px;letter-spacing:.18em;padding:7px 14px;cursor:pointer;}
  .atlas-vl-head:hover,.atlas-vl.open .atlas-vl-head{color:var(--hud-bright);
    box-shadow:inset 0 0 0 1px var(--hud-bright),inset 0 0 8px rgba(120,220,255,.20);}
  /* Bounded and scrolling: 13 rows is taller than the pane has room for
     between this button and the top edge, so the list scrolls inside its own
     box rather than growing past the window (which would leave its lower
     rows unreachable - the definition of a clipped popup). */
  .atlas-vl-body{position:absolute;right:0;bottom:calc(100% + 8px);min-width:190px;
    max-height:min(50vh,320px);overflow-y:auto;overscroll-behavior:contain;
    padding:8px;background:rgba(8,16,28,.86);box-shadow:inset 0 0 0 1px var(--hud-border);
    display:flex;flex-direction:column;gap:4px;}
  .atlas-vl-body::-webkit-scrollbar{width:6px;}
  .atlas-vl-body::-webkit-scrollbar-thumb{background:rgba(111,227,245,.3);border-radius:3px;}
  .atlas-vl-body::-webkit-scrollbar-track{background:transparent;}
  /* Open, the list stands where the pyramid credit does; the credit adds
     nothing the player needs while they are looking at this list, so it steps
     aside rather than fighting the list for the same corner. The ground
     panel gets the same room back so its own content never runs down behind
     the list either. */
  .atlas-overlay:has(.atlas-vl.open) #atlas-pyramid-credit{display:none;}
  .atlas-overlay:has(.atlas-vl.open) #atlas-sampler{
    max-height:calc(100% - var(--hud-top) - min(50vh,320px) - 64px);}
  .atlas-vl-row{display:flex;justify-content:space-between;gap:14px;width:100%;
    padding:5px 8px;border:1px solid rgba(111,227,245,.22);border-radius:3px;
    background:transparent;color:rgba(232,216,200,.55);font:inherit;font-size:9px;
    letter-spacing:.10em;cursor:pointer;transition:background .15s,color .15s,border-color .15s;}
  .atlas-vl-row:hover{border-color:rgba(111,227,245,.5);color:#e8d8c8;}
  .atlas-vl-row.on{background:rgba(111,227,245,.14);border-color:rgba(111,227,245,.6);color:#6fe3f5;}
  .atlas-vl-state{opacity:.75;}
  /* The site finder: the Layers button's twin, static so its list opens in the Layers list's slot. */
  .atlas-sf{position:static;}
  .atlas-sf.hidden,.atlas-sf-body.hidden{display:none;}
  .atlas-sf-head{display:block;background:rgba(8,16,28,.7);border:none;
    box-shadow:inset 0 0 0 1px var(--hud-border);color:var(--hud-text);
    font-family:inherit;font-size:11px;letter-spacing:.18em;padding:7px 14px;cursor:pointer;}
  .atlas-sf-head:hover,.atlas-sf.open .atlas-sf-head{color:var(--hud-bright);
    box-shadow:inset 0 0 0 1px var(--hud-bright),inset 0 0 8px rgba(120,220,255,.20);}
  .atlas-sf-body{position:absolute;right:0;bottom:calc(100% + 8px);box-sizing:border-box;
    width:min(340px,calc(100vw - 64px));max-height:min(62vh,480px);overflow-y:auto;overscroll-behavior:contain;
    padding:10px;background:rgba(10,7,6,.88);box-shadow:inset 0 0 0 1px rgba(255,180,120,.24);
    font:10px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;color:#e8d8c8;letter-spacing:.09em;}
  .atlas-sf-body .atlas-plotctl-sites{margin-top:0;padding-top:0;border-top:none;}
  .atlas-sf-body .atlas-sites-list{max-height:min(36vh,300px);}
  .atlas-overlay:has(.atlas-sf.open) #atlas-pyramid-credit{display:none;}
  .atlas-overlay:has(.atlas-sf.open) #atlas-sampler{
    max-height:calc(100% - var(--hud-top) - min(62vh,480px) - 64px);}
  /* A narrow pane has the body card and the manifest down its left half, so
     the list keeps to the right of them and under the tier readout. */
  /* Same small-pane sizing as .atlas-zoom-out, and a tighter row, so the button does not push the bar into the inspector. */
  @media (max-width:1280px),(max-height:700px){
    .atlas-bottom{gap:6px;}
    .atlas-sf-head{padding:6px 10px;font-size:10px;letter-spacing:.10em;}
    .atlas-sf-body{bottom:calc(100% + 10px);}
  }
  @media (max-width:900px){
    .atlas-sf-body{width:240px;max-height:44vh;}
    .atlas-sf-body .atlas-sites-list{max-height:24vh;}
  }
  /* The warp level row: the same ladder the sim's rail draws, sized for the
     Atlas bottom bar. */
  .atlas-warp{display:flex;align-items:center;gap:8px;position:relative;}
  .atlas-warp.hidden{display:none;}
  .atlas-warp-label{font-size:10px;letter-spacing:.16em;color:var(--hud-dim);
    min-width:66px;text-align:right;font-variant-numeric:tabular-nums;}
  .atlas-warp-bar{display:flex;gap:2px;}
  .atlas-warp-btn,.atlas-warp-goal{background:rgba(8,16,28,.7);border:none;
    box-shadow:inset 0 0 0 1px var(--hud-border);color:var(--hud-text);
    font-family:inherit;font-size:10px;letter-spacing:.10em;padding:6px 8px;cursor:pointer;}
  .atlas-warp-btn:hover,.atlas-warp-goal:hover{color:var(--hud-bright);
    box-shadow:inset 0 0 0 1px var(--hud-bright),inset 0 0 8px rgba(120,220,255,.20);}
  .atlas-warp-btn.active,.atlas-warp-goal.active{color:var(--hud-bright);background:rgba(120,220,255,.18);
    box-shadow:inset 0 0 0 1px var(--hud-bright),inset 0 0 8px rgba(120,220,255,.20);}
  /* Per button, not the whole bar: a level this instant cannot reach dims on
     its own, so the picked one stays legible even while it is the only one
     that does (docs/VERIFICATION.md, --viewwarp). */
  .atlas-warp-btn.blocked,.atlas-warp-goal.blocked{opacity:.4;cursor:default;}
  .atlas-warp-goal.hidden{display:none;}
  .atlas-warp-notice{position:absolute;right:0;bottom:calc(100% + 6px);white-space:nowrap;
    font-size:10px;font-weight:bold;letter-spacing:.06em;color:#ffd17a;
    text-shadow:0 0 6px rgba(255,180,90,.55);}
  .atlas-warp-notice.hidden{display:none;}
  /* The standing lock reason, not a fresh drop: same slot, quieter face. */
  .atlas-warp-notice.standing{font-weight:normal;color:var(--hud-dim);text-shadow:none;}
  /* Why the map is holding the clock (src/sim/mapClockHold.ts). Top centre, under
     the breadcrumb's row, at every rung: a held clock is as true in the plot
     walkaround as in the Solar System view. */
  .atlas-clock-hold{position:absolute;top:44px;left:50%;transform:translateX(-50%);
    white-space:nowrap;pointer-events:none;padding:4px 10px;border-radius:3px;
    background:rgba(4,9,16,.72);box-shadow:inset 0 0 0 1px rgba(255,180,90,.45);
    font-size:10px;font-weight:bold;letter-spacing:.08em;color:#ffd17a;
    text-shadow:0 0 6px rgba(255,180,90,.55);}
  .atlas-clock-hold.hidden{display:none;}
  /* The bottom bar shares its line with the body card in one corner and the
     imagery credit in the other, and under about 1100 px of window there is no
     room between them for a rate ladder: stacking it put the credit under the
     buttons and pushing the bar left put it over the card. The sim's own rail
     carries the same row at any width, so here the row stands down rather than
     take either one's ground. */
  @media (max-width:1100px){
    .atlas-warp{display:none;}
  }
  .atlas-plotctl-toggles{display:flex;gap:6px;margin-top:3px;}
  .atlas-plotctl-toggles button{flex:1;padding:4px 8px;border:1px solid rgba(255,180,120,.24);border-radius:3px;
    background:transparent;color:rgba(232,216,200,.55);font:inherit;letter-spacing:.10em;cursor:pointer;
    transition:background .15s,color .15s,border-color .15s;}
  .atlas-plotctl-toggles button:hover{border-color:rgba(255,180,120,.45);color:#e8d8c8;}
  .atlas-plotctl-toggles button.on{background:rgba(255,180,84,.16);border-color:rgba(255,180,84,.60);color:#ffb454;}
  .atlas-plotctl-toggles button.wide{letter-spacing:.16em;}
  .atlas-plotctl-sites{display:flex;flex-direction:column;gap:6px;margin-top:8px;padding-top:9px;
    border-top:1px solid rgba(255,180,120,.16);}
  /* Neither the chips nor the count line may size the panel. A box with no
     width of its own asks for the width its content wants, and 200 chips in a
     wrapping row ask for every pixel of the pane; size containment turns that
     around, so these take the panel's width and never set it. */
  .atlas-sites-list,.atlas-sites-head,.atlas-sites-search,.atlas-sites-facets,
  .atlas-sites-pick,.atlas-sites-pin{contain:inline-size;}
  /* site-chips-check --inject unbounded: the width bound withdrawn. */
  .atlas-plotctl.ctl-width-off{max-width:none;}
  .atlas-plotctl.ctl-width-off :is(.atlas-sites-list,.atlas-sites-head,.atlas-sites-search,
    .atlas-sites-facets,.atlas-sites-pick,.atlas-sites-pin){contain:none;}
  .atlas-sites-head{font-size:9px;letter-spacing:.12em;color:rgba(232,216,200,.45);}
  .atlas-sites-head.hidden,.atlas-sites-filter.hidden{display:none;}
  .atlas-sites-filter{box-sizing:border-box;width:100%;padding:4px 6px;
    border:1px solid rgba(111,227,245,.22);border-radius:3px;background:rgba(8,16,28,.55);
    color:#e8d8c8;font:inherit;font-size:9px;letter-spacing:.10em;}
  .atlas-sites-filter:focus{outline:none;border-color:rgba(111,227,245,.6);}
  /* Chips wrap and the LIST scrolls: one unwrapped row used to drive the
     whole panel's own horizontal scroll, taking the sliders with it.
     No overscroll-behavior of its own, so a wheel at the list's top or
     bottom carries on into the panel behind it rather than stopping dead;
     the panel's own contain is what keeps it off the page after that. The
     list keeps its own scroll rather than growing to its content, because
     the filter box and the rung sliders sit ABOVE it and one scroller would
     carry them off the top on the way to the two hundredth chip. */
  .atlas-sites-list{display:flex;flex-wrap:wrap;gap:6px;max-height:30vh;
    overflow-y:auto;}
  .atlas-sites-band{flex:0 0 100%;margin-top:2px;font-size:8px;letter-spacing:.14em;
    color:rgba(111,227,245,.50);}
  .atlas-sites-empty{flex:0 0 100%;font-size:9px;color:rgba(232,216,200,.45);}
  .atlas-plotctl-sites button{flex:1 1 auto;min-width:0;max-width:100%;
    overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
    padding:5px 7px;border:1px solid rgba(111,227,245,.22);
    border-radius:3px;background:transparent;color:rgba(232,216,200,.55);font:inherit;
    font-size:9px;letter-spacing:.10em;cursor:pointer;transition:background .15s,color .15s;}
  .atlas-plotctl-sites button:focus-visible{outline:1px solid rgba(111,227,245,.7);outline-offset:1px;}
  /* site-chips-check --inject flat-row: the single unwrapped row, restored. */
  .atlas-plotctl-sites.flat .atlas-sites-list{flex-wrap:nowrap;max-height:none;overflow:visible;}
  .atlas-plotctl-sites.flat button{flex:1;min-width:auto;overflow:visible;text-overflow:clip;white-space:normal;}
  .atlas-plotctl-sites button:hover{border-color:rgba(111,227,245,.5);color:#e8d8c8;}
  .atlas-plotctl-sites button.on{background:rgba(111,227,245,.14);border-color:rgba(111,227,245,.6);color:#6fe3f5;}
  /* Above a handful of sites: the filters, the globe pick, and a list that draws
     only the rows on screen, each absolutely placed on a spacer of the full height. */
  .atlas-sites-search{display:flex;gap:4px;}
  .atlas-sites-search.hidden{display:none;}
  .atlas-sites-search .atlas-sites-filter{flex:1 1 auto;min-width:0;width:auto;}
  .atlas-plotctl-sites .atlas-sites-more{flex:0 0 auto;}
  .atlas-sites-facets{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:4px;}
  .atlas-sites-facets.hidden,.atlas-sites-facets .hidden,.atlas-sites-pick.hidden,.atlas-sites-pin.hidden{display:none;}
  .atlas-sites-facets select{min-width:0;box-sizing:border-box;padding:3px 4px;
    border:1px solid rgba(111,227,245,.22);border-radius:3px;background:rgba(8,16,28,.85);
    color:#e8d8c8;font:inherit;font-size:9px;letter-spacing:.06em;}
  .atlas-sites-facets select:focus{outline:none;border-color:rgba(111,227,245,.6);}
  .atlas-plotctl-sites .atlas-sites-globe{grid-column:1 / -1;}
  .atlas-sites-pick{display:flex;align-items:center;justify-content:space-between;gap:6px;
    font-size:9px;letter-spacing:.08em;color:#6fe3f5;}
  .atlas-plotctl-sites .atlas-sites-pick button{flex:0 0 auto;}
  .atlas-sites-pin{display:flex;flex-direction:column;gap:4px;}
  .atlas-sites-list{--site-row-h:24px;}
  .atlas-sites-list.rows{display:block;position:relative;}
  .atlas-sites-spacer{position:relative;width:100%;}
  .atlas-plotctl-sites .atlas-sites-spacer button{position:absolute;left:0;right:0;box-sizing:border-box;
    height:var(--site-row-h);display:flex;align-items:center;justify-content:space-between;gap:8px;text-align:left;}
  .atlas-site-n{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .atlas-site-m{flex:0 0 auto;color:rgba(111,227,245,.55);font-size:8px;}
  .atlas-card-row i{display:inline-block;width:7px;height:7px;margin-right:6px;border-radius:1px;vertical-align:middle;}
  .atlas-card-row i.v-prime{background:#5bf29e;}
  .atlas-card-row i.v-workable{background:#70ccfa;}
  .atlas-card-row i.v-marginal{background:#ffb454;}
  .atlas-card-row i.v-unbuildable{background:#f25750;}
  .atlas-warn{color:#f25750;}
  .atlas-parcel{margin-top:10px;padding-top:9px;border-top:1px solid rgba(255,180,84,.30);}
  .atlas-parcel-name{color:#ffd17a;font-size:13px;letter-spacing:.10em;margin-bottom:6px;}
  .atlas-held{color:#ffd17a;}
  .atlas-mono{font-size:9px;letter-spacing:.02em;color:rgba(232,216,200,.75);}
  .atlas-claim{display:block;width:100%;margin-top:9px;padding:8px 10px;
    border:1px solid rgba(255,209,122,.65);border-radius:3px;background:rgba(255,209,122,.15);
    color:#ffd17a;font:inherit;font-size:10px;letter-spacing:.14em;cursor:pointer;
    transition:background .15s,border-color .15s;}
  .atlas-claim:hover{background:rgba(255,209,122,.30);border-color:#ffd17a;}
  .atlas-claim.release{border-color:rgba(216,210,198,.30);background:transparent;color:rgba(216,210,198,.55);}
  .atlas-claim.release:hover{border-color:rgba(255,95,86,.6);color:#ff9a94;background:rgba(255,95,86,.10);}

  /* The sampler is taller than a laptop viewport once a cell is selected, so
     it scrolls and its sections fold. The header stays put above the scroll
     so the minimize button is always reachable, never carried off with the
     content it collapses. */
  /* The panel's own clearance, floored so a taller-than-usual credit line
     (a body citing three sources rather than one) still leaves it clear
     rather than growing the panel down over it. On #atlas-sampler itself,
     not the body: a percentage max-height on the body never resolves, since
     its containing block (the sampler) has no height of its own to measure
     against - only the sampler is a direct child of the overlay box a
     percentage can actually read. */
  #atlas-sampler{max-height:calc(100% - var(--hud-top) - max(var(--hud-bot), 56px + var(--credit-h) + 12px));}
  .atlas-sampler-body{min-height:0;overflow-y:auto;overscroll-behavior:contain;}
  /* The cell you picked, and the two buttons that act on it, come first. Left in
     document order the descent button lands a screen and a half down a panel
     that is already scrolling, which is the only route from a claim to a plot. */
  .atlas-sect{margin-top:9px;order:1;}
  .atlas-sect:has(> .atlas-sect-head[data-sect="cell"]){order:0;}
  .atlas-sect-body{display:flex;flex-direction:column;}
  .atlas-descend{order:-2;}
  .atlas-parcel{order:-1;}
  .atlas-sect-body > .atlas-card-hint{order:-2;}
  .atlas-sect-head{display:flex;align-items:center;justify-content:space-between;width:100%;
    padding:4px 0;border:0;border-top:1px solid rgba(111,227,245,.16);background:none;
    color:#6fe3f5;font:inherit;font-size:10px;letter-spacing:.16em;cursor:pointer;text-align:left;}
  .atlas-sect-head:hover{color:#a8f0ff;}
  .atlas-sect-head i{width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;
    border-top:5px solid currentColor;transition:transform .15s;}
  .atlas-sect.closed .atlas-sect-head i{transform:rotate(-90deg);}
  .atlas-sect.closed .atlas-sect-body{display:none;}

  /* Drag handles come from the shared HUD stylesheet (index.html), which is
     where the naval sim's widgets get theirs.

     This file used to carry its OWN copy of .widget-handle, and the copy named
     the panels it revealed on hover one class at a time — so the telemetry
     panel was wired draggable but its handle could never become visible, and
     any panel added later inherited the same silence. The shared rule is
     written against [data-widget], which is what makeDraggable actually sets,
     so every panel gets a handle for free and none can be forgotten. */
  .atlas-plotctl,.atlas-sampler{position:absolute;}
  /* Amber to match the Atlas, overriding only the colour of the shared rule. */
  .atlas-root .widget-handle{
    background:linear-gradient(180deg,rgba(255,180,120,.20),transparent);
    border-radius:4px 4px 0 0;}
  .atlas-root .widget-handle:hover{
    background:linear-gradient(180deg,rgba(255,180,120,.55),rgba(255,180,120,.06));}
  .atlas-resetpanels{position:absolute;right:176px;top:6px;z-index:60;
    padding:4px 9px;border:1px solid rgba(255,180,84,.35);border-radius:3px;
    background:rgba(10,16,26,.72);color:rgba(232,216,200,.55);
    font:inherit;font-size:9px;letter-spacing:.12em;cursor:pointer;opacity:0;
    transition:opacity .15s,color .15s,border-color .15s;}
  .atlas-root:hover .atlas-resetpanels{opacity:1;}
  .atlas-resetpanels:hover{color:#ffb454;border-color:#ffb454;}
  .atlas-descend{display:block;width:100%;margin-top:10px;padding:7px 10px;
    border:1px solid rgba(255,180,84,.55);border-radius:3px;background:rgba(255,180,84,.13);
    color:#ffb454;font:inherit;font-size:10px;letter-spacing:.12em;cursor:pointer;
    transition:background .15s,border-color .15s;}
  .atlas-descend:hover{background:rgba(255,180,84,.26);border-color:#ffb454;}
  /* Two columns rather than the toggle row's usual four-across: a REMOVE
     label ("REMOVE EXTRACTOR") needs the extra width to stay on one line. */
  #pc-forge-row{display:grid;grid-template-columns:1fr 1fr;}
  .forge-btn{font-size:11px !important;letter-spacing:.06em !important;
    white-space:nowrap;overflow:hidden;text-overflow:clip;}
  .forge-btn:disabled{opacity:.35;cursor:not-allowed !important;}
  .forge-toast{position:fixed;bottom:80px;left:50%;transform:translateX(-50%) translateY(20px);
    padding:10px 22px;border:1px solid rgba(120,220,255,.45);border-radius:4px;
    background:rgba(8,16,28,.88);backdrop-filter:blur(8px);
    color:#aff;font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;
    letter-spacing:.08em;text-shadow:0 0 5px rgba(120,220,255,.5);
    z-index:200;opacity:0;transition:opacity .3s,transform .3s;pointer-events:none;}
  .forge-toast.show{opacity:1;transform:translateX(-50%) translateY(0);}
  .forge-toast-error{border-color:rgba(255,100,100,.55);color:#ff9a94;text-shadow:0 0 5px rgba(255,80,80,.4);}
  /* A toast carrying an action has to be reachable, unlike the notices above. */
  .forge-toast-act{pointer-events:auto;}
  .forge-toast-btn{margin-left:12px;padding:3px 10px;border-radius:3px;cursor:pointer;
    border:1px solid rgba(255,209,122,.55);background:rgba(255,209,122,.14);
    color:#ffd17a;font:inherit;letter-spacing:.08em;}
  .forge-toast-btn:hover{background:rgba(255,209,122,.28);border-color:#ffd17a;}
  /* The label over a placed building, beside the cursor. Pewter on near-black
     with a copper name, the palette the buildings themselves are drawn in. */
  .atlas-struct-label{position:fixed;z-index:210;pointer-events:none;min-width:170px;max-width:300px;
    padding:7px 11px 8px;border:1px solid rgba(190,170,150,.38);border-left:2px solid #b87a4b;border-radius:3px;
    background:rgba(11,11,12,.88);backdrop-filter:blur(6px);
    color:#c9c6bf;font:10px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:.07em;}
  .atlas-struct-label[hidden]{display:none;}
  .atlas-struct-label .sl-name{color:#e0a36f;font-size:11px;letter-spacing:.05em;margin-bottom:2px;}
  .atlas-struct-label .sl-row{color:#a9a69f;}
  .atlas-struct-label .sl-live{color:#8fe3b0;}
  .atlas-struct-label .sl-warn{color:#ffb454;}
  .atlas-struct-label .sl-ready{color:#e8e2c8;}
  /* ── The body manifest ──────────────────────────────────────────────────
     The left column above the body card, at the orrery rungs. Bounded by the
     readout slot below it for the same reason the plot controls are: unbounded
     it grows past the pane and takes its own rows out of reach. */
  .atlas-manifest{position:absolute;left:16px;top:var(--hud-top);z-index:6;
    box-sizing:border-box;width:var(--hud-col);
    max-height:calc(100% - var(--hud-top) - var(--hud-insp) - 28px);
    display:flex;flex-direction:column;min-height:0;
    padding:6px 12px 8px;border:1px solid rgba(111,227,245,.20);border-radius:5px;
    background:rgba(6,12,22,.80);backdrop-filter:blur(9px);
    font:10px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;color:#cfe6ee;letter-spacing:.06em;}
  .atlas-manifest.hidden{display:none;}
  .atlas-manifest > .atlas-sect{margin-top:0;display:flex;flex-direction:column;min-height:0;}
  .atlas-manifest .atlas-sect-head{border-top:0;}
  /* The list scrolls inside its own box, never the page. */
  .atlas-manifest .atlas-sect-body{overflow-y:auto;overscroll-behavior:contain;min-height:0;}
  .atlas-manifest .atlas-sect-body::-webkit-scrollbar{width:8px;}
  .atlas-manifest .atlas-sect-body::-webkit-scrollbar-thumb{background:rgba(111,227,245,.22);border-radius:4px;}
  .atlas-manifest .atlas-sect-body::-webkit-scrollbar-track{background:transparent;}
  .atlas-mf-row{display:flex;align-items:stretch;gap:4px;
    padding-left:calc(var(--mf-depth,0) * 12px);}
  .atlas-mf-row.focused .atlas-mf-label{color:#ffb454;}
  .atlas-mf-name{flex:1 1 auto;min-width:0;display:flex;align-items:baseline;gap:6px;
    padding:3px 4px;border:0;border-radius:2px;background:none;color:inherit;font:inherit;
    text-align:left;cursor:pointer;}
  .atlas-mf-name:hover{background:rgba(111,227,245,.12);color:#a8f0ff;}
  .atlas-mf-name:focus-visible{outline:1px solid #6fe3f5;outline-offset:-1px;}
  .atlas-mf-label{flex:0 0 auto;letter-spacing:.10em;}
  .atlas-mf-class{flex:0 0 auto;font-size:9px;color:rgba(207,230,238,.45);letter-spacing:.02em;}
  .atlas-mf-chip{flex:1 1 auto;min-width:0;text-align:right;font-size:8px;letter-spacing:.02em;
    color:rgba(143,208,224,.70);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .atlas-mf-more{flex:0 0 auto;width:18px;display:flex;align-items:center;justify-content:center;
    padding:0;border:0;background:none;color:rgba(143,208,224,.55);cursor:pointer;}
  .atlas-mf-more:hover{color:#a8f0ff;}
  .atlas-mf-more:focus-visible{outline:1px solid #6fe3f5;outline-offset:-1px;}
  .atlas-mf-more i{width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;
    border-top:5px solid currentColor;transform:rotate(-90deg);transition:transform .15s;}
  .atlas-mf-more.open i{transform:rotate(0);}
  .atlas-mf-detail{margin:2px 0 6px calc(var(--mf-depth,0) * 12px + 8px);
    padding-left:8px;border-left:1px solid rgba(111,227,245,.18);}
  .atlas-mf-fact{display:flex;justify-content:space-between;gap:8px;font-size:9px;letter-spacing:.02em;}
  .atlas-mf-fact > span:first-child{color:rgba(207,230,238,.45);flex:0 0 auto;}
  .atlas-mf-fact > span:last-child{text-align:right;min-width:0;}
  .atlas-mf-reason{margin-top:4px;font-size:8px;line-height:1.5;letter-spacing:0;
    color:rgba(207,230,238,.42);}
  .atlas-mf-empty{padding:4px;font-size:9px;color:rgba(207,230,238,.45);}

  /* Shares a row with the tier chip (atlas.css .atlas-tier), so it sits flush left of it, clear of the panels below. */
  .atlas-top-chips{display:flex;align-items:stretch;gap:8px;flex:0 0 auto;}
  .atlas-badge{display:flex;align-items:center;white-space:nowrap;
    padding:0 10px;border:1px solid rgba(255,200,100,.35);border-radius:3px;
    background:rgba(10,7,6,.75);backdrop-filter:blur(6px);
    font:10px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;color:#ffb454;
    letter-spacing:.08em;pointer-events:none;}
  .atlas-badge.hidden{display:none;}

  /* Small panes. Same breakpoint as the base HUD in index.html, which sets the
     column width and the readout slot these read. */
  @media (max-width:1280px),(max-height:700px){
    .atlas-plotctl{width:var(--hud-col);padding:9px 10px;gap:4px;
      font-size:9px;letter-spacing:.04em;}
    .atlas-plotctl label,.atlas-rockctl label{grid-template-columns:38px minmax(0,1fr) 26px;gap:6px;}
    .atlas-plotctl-toggles{gap:4px;}
    .atlas-plotctl-toggles button{padding:3px 5px;letter-spacing:.04em;}
    .atlas-plotctl-sites{margin-top:6px;padding-top:7px;}
    .atlas-plotctl-sites button{font-size:8px;padding:3px 4px;}
    .atlas-sites-list{max-height:26vh;}
    /* This column is narrower than a REMOVE label's own nowrap fix can hold
       at any font size worth reading, so it keeps the older two-line wrap
       rather than clip a building's name off. */
    .atlas-plotctl .forge-btn{font-size:9px !important;white-space:normal !important;
      overflow:visible !important;text-overflow:clip !important;}
    .atlas-sampler{width:var(--hud-col);}
    /* Telemetry drops in under the sampler, which is already scrolling at this
       size, and leaves the left column whole for the controls. A fixed offset
       here rather than --hud-bot: the two share this one column at this size,
       so growing --hud-bot for the credit line above the zoom-out bar would
       otherwise push telemetry up into the sampler it sits right against. */
    .atlas-telem{left:auto;right:18px;bottom:48px;width:var(--hud-col);
      max-height:calc(50% - 60px);}
    .atlas-overlay:has(#atlas-telem:not(.hidden)) #atlas-sampler{max-height:calc(50% - 60px);}
    /* The plot tiers hide .atlas-inspector at this size (index.html), which
       frees its bottom-left corner - the one spot the sampler and telemetry,
       both right-anchored here, never reach. */
    .atlas-overlay:has(#atlas-plotctl:not(.hidden)) #atlas-pyramid-credit{
      right:auto;left:18px;bottom:16px;text-align:left;max-width:220px;}
  }

  /* A finger needs 44 px of chip to land on, and the filter box needs the same.
     Outside this query the desktop panel keeps the height it always had. */
  @media (pointer: coarse),(max-width:900px){
    .atlas-plotctl-sites button{min-height:44px;}
    .atlas-sites-filter{min-height:44px;}
    /* The header gives the button its 44 px rather than the button overhanging
       into the drag strip above it, which would take the press. */
    .atlas-plotctl .hud-min-btn{min-width:44px;min-height:44px;margin:0;}
    /* Shorter than the desktop cap, not taller: the chips are twice the height
       here, so the same share of a small viewport buys fewer of them. */
    .atlas-sites-list{gap:8px;max-height:24vh;--site-row-h:44px;}
    .atlas-sites-facets select{min-height:44px;}
  }

  /* ── Event log ──────────────────────────────────────────────────────── */
  .atlas-event-log{position:absolute;left:16px;bottom:52px;z-index:6;
    max-width:420px;
    font:10px/1.55 ui-monospace,SFMono-Regular,Menlo,monospace;
    color:rgba(207,230,238,.70);letter-spacing:.05em;pointer-events:auto;}
  .atlas-log-row{padding:2px 8px;border-radius:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
    transition:background .12s;}
  .atlas-log-row.clickable{cursor:pointer;}
  .atlas-log-row.clickable:hover{background:rgba(111,227,245,.12);color:#cfe6ee;}
  .atlas-log-time{color:rgba(143,208,224,.55);margin-right:6px;}

  /* ── Tier badges ────────────────────────────────────────────────────── */
  .atlas-tier-badge{display:inline-block;padding:1px 6px;border-radius:2px;
    font-size:9px;letter-spacing:.12em;font-weight:600;}
  .atlas-tier-badge.tier-orbit{background:rgba(255,180,84,.22);border:1px solid rgba(255,180,84,.55);color:#ffb454;}
  .atlas-tier-badge.tier-stereo{background:rgba(143,208,224,.18);border:1px solid rgba(143,208,224,.45);color:#8fd0e0;}
  .atlas-tier-badge.tier-surveyed{background:rgba(91,242,158,.16);border:1px solid rgba(91,242,158,.45);color:#5bf29e;}

  /* ── Survey button ──────────────────────────────────────────────────── */
  .atlas-survey-btn{display:block;width:100%;margin-top:8px;padding:6px 10px;
    border:1px solid rgba(143,208,224,.55);border-radius:3px;background:rgba(143,208,224,.12);
    color:#8fd0e0;font:inherit;font-size:10px;letter-spacing:.12em;cursor:pointer;
    transition:background .15s,border-color .15s;}
  .atlas-survey-btn:hover{background:rgba(143,208,224,.26);border-color:#8fd0e0;}

  /* ── Buildings on the selected cell ───────────────────────────────── */
  .atlas-bldgs{margin:2px 0 8px;padding:6px 8px;border:1px solid rgba(255,209,122,.35);border-radius:3px;
    background:rgba(255,209,122,.06);}
  .atlas-bldgs-title{font-size:9px;letter-spacing:.2em;color:#ffd17a;margin-bottom:4px;}
  .atlas-bldg{padding:4px 0;border-top:1px solid rgba(143,208,224,.12);}
  .atlas-bldg:first-of-type{border-top:none;}
  .atlas-bldg-head{display:flex;justify-content:space-between;gap:8px;font-size:11px;}
  .atlas-bldg-line{font-size:10px;color:rgba(207,230,238,.8);margin-top:2px;}
  .atlas-bldg-ready{color:#5bf29e;margin-left:6px;}
  .atlas-forge-btn{margin-top:4px;padding:3px 8px;border:1px solid rgba(255,209,122,.55);border-radius:3px;
    background:rgba(255,209,122,.12);color:#ffd17a;font:inherit;font-size:9.5px;letter-spacing:.12em;cursor:pointer;}
  .atlas-forge-btn:hover{background:rgba(255,209,122,.26);}

  /* Disabled stake button */
  .atlas-claim:disabled{opacity:.40;cursor:not-allowed;pointer-events:auto;}

  /* Phase 7's 44px touch-target bar, scoped to touch input or a narrow
     window so 1600x900 and wider still draw exactly as before. The manifest
     list and plot controls already scroll inside their own box, so nothing
     overflows - fewer rows show at once, which is the cost of the bar. */
  @media (pointer: coarse), (max-width: 900px) {
    /* The breadcrumb grows into the panel row's own top clearance - give the
       panels their start back before growing the crumbs. */
    .atlas-overlay{--hud-top:70px;}
    .atlas-mf-row{min-height:44px;}
    .atlas-mf-more{width:44px;}
    /* Negative margin, not a taller box: .atlas-bottom's own row height stays
       what it was, so nothing below or above it has to move for these two. */
    .atlas-vl-head{box-sizing:border-box;height:44px;margin:-10px 0;}
    #atlas-plotctl button{min-height:44px;}
    .atlas-plotctl label{min-height:44px;}
    .atlas-plotctl input[type=range]{height:44px;background:transparent;}
    .atlas-plotctl input[type=range]::-webkit-slider-runnable-track{
      height:2px;background:rgba(255,180,120,.26);}
    .atlas-sect-head,.atlas-claim,.atlas-descend,.atlas-survey-btn,.atlas-forge-btn{min-height:44px;}
    /* The inspector is not drawn at the plot tiers, so the sampler can run
       down to the bar itself instead of stopping short of a readout slot that
       is not there. 52px is what the bar needs once its buttons stand 10px
       proud of their own row. The plot controls share their column's bottom
       with the pyramid credit and so cannot take the same room. */
    .atlas-overlay:has(#atlas-plotctl:not(.hidden)){--hud-bot:52px;}
    /* Telemetry's bottom edge sat 2px into those same proud buttons. */
    .atlas-telem{bottom:56px;max-height:calc(50% - 68px);}
  }
  `;
  document.head.appendChild(s);
}
